'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRole, ROLE_LABELS, ROLE_COLORS, SupplierRoleType } from '@/contexts/RoleContext';
import {
  Shield, Users, BarChart2, Package, Truck, Calculator,
  Plus, Edit2, Trash2, Eye, EyeOff, CheckCircle, XCircle,
  Key, Phone, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface StaffRow {
  role_id: number;
  supplier_id: number;
  staff_name: string;
  role_type: SupplierRoleType;
  pin_code: string;
  phone_whatsapp: string | null;
  is_active: boolean;
  permissions: Record<string, boolean>;
}

const ROLE_ICONS: Record<SupplierRoleType, React.ElementType> = {
  Owner_Admin: Shield,
  Sales_Manager: BarChart2,
  Sales_Rep: Users,
  Warehouse_Manager: Package,
  Driver: Truck,
  Accountant: Calculator,
};

const PERMISSION_KEYS = [
  { key: 'crm', label: 'CRM الزبائن' },
  { key: 'price_lists', label: 'قوائم الأسعار' },
  { key: 'promotions', label: 'الحملات' },
  { key: 'sales_reps', label: 'المندوبون' },
  { key: 'fifo', label: 'مخزون FIFO' },
  { key: 'delivery_schedule', label: 'جدولة التوصيل' },
  { key: 'delivery_confirmation', label: 'تأكيد الاستلام' },
  { key: 'fleet', label: 'إدارة الأسطول' },
  { key: 'returns', label: 'المرتجعات' },
  { key: 'debt_collection', label: 'تحصيل الديون' },
  { key: 'cheques', label: 'الشيكات' },
  { key: 'tva_report', label: 'تقرير TVA' },
  { key: 'kpi', label: 'لوحة KPI' },
  { key: 'purchase_orders', label: 'أوامر الشراء' },
  { key: 'profitability', label: 'تقارير الربحية' },
  { key: 'order_sheet', label: 'تسجيل طلب' },
];

export default function SupplierRolesContent() {
  const { language } = useLanguage();
  const { hasPermission } = useRole();
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPins, setShowPins] = useState<Record<number, boolean>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({
    staff_name: '',
    role_type: 'Sales_Rep' as SupplierRoleType,
    pin_code: '',
    phone_whatsapp: '',
    is_active: true,
    permissions: {} as Record<string, boolean>,
  });

  const supabase = createClient();

  const fetchStaff = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('supplier_staff_roles')
      .select('*')
      .order('role_id');
    if (!error && data) setStaff(data as StaffRow[]);
    setLoading(false);
  };

  useEffect(() => { fetchStaff(); }, []);

  const togglePin = (id: number) => {
    setShowPins(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = async () => {
    if (!form.staff_name || !form.pin_code) {
      toast.error('الاسم وكود PIN مطلوبان');
      return;
    }
    if (form.pin_code.length < 4) {
      toast.error('كود PIN يجب أن يكون 4 أرقام على الأقل');
      return;
    }

    const payload = {
      staff_name: form.staff_name,
      role_type: form.role_type,
      pin_code: form.pin_code,
      phone_whatsapp: form.phone_whatsapp || null,
      is_active: form.is_active,
      permissions: form.permissions,
      supplier_id: staff[0]?.supplier_id || 1,
    };

    if (editingId) {
      const { error } = await supabase
        .from('supplier_staff_roles')
        .update(payload)
        .eq('role_id', editingId);
      if (!error) {
        toast.success('تم تحديث الدور بنجاح');
        setEditingId(null);
        setShowAddForm(false);
        fetchStaff();
      }
    } else {
      const { error } = await supabase
        .from('supplier_staff_roles')
        .insert(payload);
      if (!error) {
        toast.success('تم إضافة الدور بنجاح');
        setShowAddForm(false);
        fetchStaff();
      }
    }
    setForm({ staff_name: '', role_type: 'Sales_Rep', pin_code: '', phone_whatsapp: '', is_active: true, permissions: {} });
  };

  const handleEdit = (row: StaffRow) => {
    setForm({
      staff_name: row.staff_name,
      role_type: row.role_type,
      pin_code: row.pin_code,
      phone_whatsapp: row.phone_whatsapp || '',
      is_active: row.is_active,
      permissions: row.permissions,
    });
    setEditingId(row.role_id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: number) => {
    const { error } = await supabase
      .from('supplier_staff_roles')
      .delete()
      .eq('role_id', id);
    if (!error) {
      toast.success('تم حذف الدور');
      fetchStaff();
    }
  };

  const togglePermission = (key: string) => {
    setForm(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  };

  return (
    <AppLayout
      title={language === 'ar' ? 'إدارة الأدوار والصلاحيات' : 'Gestion des Rôles & Permissions'}
      subtitle={language === 'ar' ? 'كود PIN خاص لكل دور — وصول محدود لواجهته' : 'Code PIN unique par rôle — accès limité à son interface'}
      role="supplier"
    >
      <div className="space-y-6 fade-in">
        {/* Header actions */}
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            {(['Owner_Admin','Sales_Manager','Sales_Rep','Warehouse_Manager','Driver','Accountant'] as SupplierRoleType[]).map(role => {
              const Icon = ROLE_ICONS[role];
              const count = staff.filter(s => s.role_type === role).length;
              return (
                <div key={role} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-600 ${ROLE_COLORS[role]}`}>
                  <Icon size={12} />
                  <span>{count}</span>
                </div>
              );
            })}
          </div>
          {hasPermission('all') && (
            <button
              onClick={() => { setShowAddForm(true); setEditingId(null); setForm({ staff_name: '', role_type: 'Sales_Rep', pin_code: '', phone_whatsapp: '', is_active: true, permissions: {} }); }}
              className="btn-primary text-xs py-2 px-3"
            >
              <Plus size={13} />
              {language === 'ar' ? 'إضافة دور' : 'Ajouter Rôle'}
            </button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="card-base border-primary/30">
            <h3 className="text-sm font-700 text-foreground mb-4">
              {editingId ? (language === 'ar' ? 'تعديل الدور' : 'Modifier le Rôle') : (language === 'ar' ? 'إضافة دور جديد' : 'Nouveau Rôle')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1">
                  {language === 'ar' ? 'الاسم الكامل' : 'Nom Complet'}
                </label>
                <input
                  value={form.staff_name}
                  onChange={e => setForm(p => ({ ...p, staff_name: e.target.value }))}
                  className="input-base w-full text-sm"
                  placeholder={language === 'ar' ? 'اسم الموظف' : 'Nom de l\'employé'}
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1">
                  {language === 'ar' ? 'الدور' : 'Rôle'}
                </label>
                <select
                  value={form.role_type}
                  onChange={e => setForm(p => ({ ...p, role_type: e.target.value as SupplierRoleType }))}
                  className="input-base w-full text-sm"
                >
                  {(Object.keys(ROLE_LABELS) as SupplierRoleType[]).map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r].ar} — {ROLE_LABELS[r].fr}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1">
                  {language === 'ar' ? 'كود PIN' : 'Code PIN'}
                </label>
                <input
                  value={form.pin_code}
                  onChange={e => setForm(p => ({ ...p, pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  className="input-base w-full text-sm font-mono"
                  placeholder="4-6 أرقام"
                  type="text"
                  maxLength={6}
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1">
                  {language === 'ar' ? 'رقم WhatsApp' : 'WhatsApp'}
                </label>
                <input
                  value={form.phone_whatsapp}
                  onChange={e => setForm(p => ({ ...p, phone_whatsapp: e.target.value }))}
                  className="input-base w-full text-sm"
                  placeholder="+212 6XX-XXXXXX"
                />
              </div>
            </div>

            {/* Permissions */}
            <div className="mb-4">
              <label className="block text-xs font-700 text-muted-foreground mb-2 uppercase tracking-wider">
                {language === 'ar' ? 'الصلاحيات' : 'Permissions'}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {PERMISSION_KEYS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.permissions[key]}
                      onChange={() => togglePermission(key)}
                      className="w-3.5 h-3.5 rounded accent-primary"
                    />
                    <span className="text-xs text-foreground">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={handleSave} className="btn-primary text-xs py-2 px-4">
                {language === 'ar' ? 'حفظ' : 'Enregistrer'}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setEditingId(null); }}
                className="btn-outline text-xs py-2 px-4"
              >
                {language === 'ar' ? 'إلغاء' : 'Annuler'}
              </button>
            </div>
          </div>
        )}

        {/* Staff Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-primary" />
          </div>
        ) : (
          <div className="card-base overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-right px-4 py-3 text-xs font-700 text-muted-foreground">{language === 'ar' ? 'الموظف' : 'Employé'}</th>
                    <th className="text-right px-4 py-3 text-xs font-700 text-muted-foreground">{language === 'ar' ? 'الدور' : 'Rôle'}</th>
                    <th className="text-right px-4 py-3 text-xs font-700 text-muted-foreground">{language === 'ar' ? 'كود PIN' : 'Code PIN'}</th>
                    <th className="text-right px-4 py-3 text-xs font-700 text-muted-foreground">WhatsApp</th>
                    <th className="text-right px-4 py-3 text-xs font-700 text-muted-foreground">{language === 'ar' ? 'الصلاحيات' : 'Permissions'}</th>
                    <th className="text-right px-4 py-3 text-xs font-700 text-muted-foreground">{language === 'ar' ? 'الحالة' : 'Statut'}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {staff.map(row => {
                    const Icon = ROLE_ICONS[row.role_type];
                    const permCount = Object.values(row.permissions).filter(Boolean).length;
                    return (
                      <tr key={row.role_id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${ROLE_COLORS[row.role_type]}`}>
                              <Icon size={14} />
                            </div>
                            <div>
                              <p className="text-xs font-700 text-foreground">{row.staff_name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-2xs font-600 px-2 py-0.5 rounded-full border ${ROLE_COLORS[row.role_type]}`}>
                            {ROLE_LABELS[row.role_type]?.ar}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-700 text-foreground">
                              {showPins[row.role_id] ? row.pin_code : '••••'}
                            </span>
                            <button onClick={() => togglePin(row.role_id)} className="text-muted-foreground hover:text-foreground">
                              {showPins[row.role_id] ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone size={11} />
                            <span>{row.phone_whatsapp || '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {row.permissions?.all ? (
                            <span className="text-xs font-600 text-purple-600">كل الصلاحيات</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">{permCount} صلاحية</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.is_active ? (
                            <span className="flex items-center gap-1 text-xs text-success font-600">
                              <CheckCircle size={12} /> نشط
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground font-600">
                              <XCircle size={12} /> معطل
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                              <Edit2 size={13} />
                            </button>
                            {row.role_type !== 'Owner_Admin' && (
                              <button onClick={() => handleDelete(row.role_id)} className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PIN Access Info */}
        <div className="card-base bg-primary-light border-primary/20">
          <div className="flex items-start gap-3">
            <Key size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-700 text-foreground mb-1">
                {language === 'ar' ? 'كيف يعمل نظام PIN؟' : 'Comment fonctionne le système PIN ?'}
              </p>
              <p className="text-xs text-muted-foreground">
                {language === 'ar' ?'كل موظف يدخل كوده الخاص في شاشة تسجيل الدخول ليصل فقط للواجهة المخصصة له. المالك/Admin يرى كل شيء، بينما السائق يرى فقط جدول التوصيل وتأكيد الاستلام.' :'Chaque employé entre son code PIN unique pour accéder uniquement à son interface dédiée. Le propriétaire/Admin voit tout, tandis que le chauffeur voit uniquement son planning de livraison.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
